// JSON-LD structured-data builders. Pure functions — no React.
import { SITE_NAME, SITE_URL, SITE_DESCRIPTION, absUrl } from "./site";
import { ALPHA_GAL_SYNDROME, type Entity } from "./entities";

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: SITE_DESCRIPTION,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/states?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    description: "Educational resource for Lone Star tick and alpha-gal syndrome awareness.",
    logo: absUrl("/apple-touch-icon.png"),
  };
}

export function articleJsonLd(opts: {
  headline: string;
  description: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
  section?: string;
  keywords?: string[];
  image?: string;
  /** What the article is about: a tick, an illness, a place (see ./entities). */
  about?: Entity[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    dateModified: opts.dateModified ?? opts.datePublished,
    ...(opts.section ? { articleSection: opts.section } : {}),
    ...(opts.keywords?.length ? { keywords: opts.keywords.join(", ") } : {}),
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: absUrl("/apple-touch-icon.png") },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(opts.path) },
    ...(opts.about?.length ? { about: opts.about } : {}),
  };
}

/**
 * MedicalWebPage — a stronger E-E-A-T signal than a plain Article for YMYL health
 * content (Google favors it for medical topics). `reviewedBy` is wired for when a named
 * clinician/entomologist advisor is recruited (see docs/GROWTH.md); omit it until then.
 */
export function medicalWebPageJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  lastReviewed?: string;
  image?: string;
  /** What the page is about (see ./entities). Defaults to alpha-gal syndrome. */
  about?: Entity[];
  reviewedBy?: { name: string; jobTitle?: string };
}) {
  const about = opts.about?.length ? opts.about : [ALPHA_GAL_SYNDROME];
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: opts.name,
    description: opts.description,
    url: absUrl(opts.path),
    inLanguage: "en-US",
    isFamilyFriendly: true,
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.lastReviewed ? { lastReviewed: opts.lastReviewed } : {}),
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(opts.path) },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: absUrl("/apple-touch-icon.png") },
    },
    ...(opts.reviewedBy
      ? {
          reviewedBy: {
            "@type": "Person",
            name: opts.reviewedBy.name,
            ...(opts.reviewedBy.jobTitle ? { jobTitle: opts.reviewedBy.jobTitle } : {}),
          },
        }
      : {}),
    about: about.length === 1 ? about[0] : about,
  };
}

/**
 * HowTo — eligible for HowTo rich results. Use for tick removal and prevention steps.
 */
export function howToJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  steps: { name: string; text: string }[];
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: opts.name,
    description: opts.description,
    ...(opts.image ? { image: opts.image } : {}),
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(opts.path) },
    step: opts.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export interface Crumb {
  name: string;
  path: string;
}

export function breadcrumbJsonLd(items: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absUrl(c.path),
    })),
  };
}

/**
 * Dataset, for a page that publishes a table of numbers (the range guide's CDC county
 * counts). `isBasedOn` names the file the numbers were counted from.
 */
export function datasetJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  isBasedOn: string;
  temporalCoverage: string;
  variableMeasured: string[];
  keywords?: string[];
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: opts.name,
    description: opts.description,
    url: absUrl(opts.path),
    isBasedOn: opts.isBasedOn,
    temporalCoverage: opts.temporalCoverage,
    spatialCoverage: { "@type": "Country", name: "United States" },
    variableMeasured: opts.variableMeasured,
    ...(opts.keywords?.length ? { keywords: opts.keywords } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
    isAccessibleForFree: true,
    creator: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  };
}
